import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  getIntegrations,
  getAvailableIntegrations,
  getNewIntegrationsFields,
  createIntegrations,
  createOauthIntegrations,
  getCalenders,
  deleteIntegration
} from './api';
import { AvailableIntegrationResponse, IntegrationResponse } from './types';
import { useParams } from 'react-router-dom';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { queryClient } from 'providers/AllProviders';

export function useGetAvailableIntegrations(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: AvailableIntegrationResponse[]; meta: MetaApiResponse }, Error>
) {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  return useQuery<{ data: AvailableIntegrationResponse[]; meta: MetaApiResponse }>({
    queryKey: ['availableIntegration', workspaceId, params],
    queryFn: () => getAvailableIntegrations(workspaceId!, params),
    enabled: Boolean(workspaceId),
    ...options
  });
}

export function useGetNewIntegrationsFields(code: string, options?: UseQueryOptions<any, Error>) {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  return useQuery<any>({
    queryKey: ['newIntegrationField', workspaceId, code],
    queryFn: () => getNewIntegrationsFields(code, workspaceId!),
    enabled: Boolean(workspaceId && code),
    ...options
  });
}

export function useGetIntegrations(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: IntegrationResponse[]; meta: MetaApiResponse }, Error>
) {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  return useQuery<{ data: IntegrationResponse[]; meta: MetaApiResponse }>({
    queryKey: ['integrations', workspaceId, params],
    queryFn: () => getIntegrations(workspaceId!, params),
    enabled: Boolean(workspaceId),
    ...options
  });
}

export function useCreateIntegration(options?: UseMutationOptions<any, Error>) {
  const { workspaceId } = useParams();

  return useMutation({
    mutationFn: (payload: any) => createIntegrations(workspaceId!, payload),
    ...options,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({
        queryKey: ['integrations'],
        refetchType: 'active'
      });
    }
  });
}

export function useCreateOauthIntegration(options?: UseMutationOptions<any, Error>) {
  const { workspaceId } = useParams();
  const payload = {
    oauth_state: {
      workspace_id: Number(workspaceId),
      redirect_uri: window.location.href
    }
  };
  return useMutation({
    mutationFn: (code: any) => createOauthIntegrations(code, payload),
    ...options,
    onSuccess: (data: any) => {
      if (data && data.url) {
        window.open(data.url);
      }
      queryClient.invalidateQueries({
        queryKey: ['availableIntegration'],
        refetchType: 'active'
      });
    }
  });
}

export function useGetCalenders(
  id: number,
  actionType: string,
  options?: UseQueryOptions<{ value: number; label: string }[], Error>
) {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  return useQuery<{ value: number; label: string }[]>({
    queryKey: ['calenders', workspaceId, id],
    queryFn: () => getCalenders(workspaceId!, id!),
    enabled: Boolean(workspaceId && id && actionType === 'book_appointment'),
    ...options
  });
}

export function useDeleteIntegration() {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  return useMutation({
    mutationFn: (id: number) => deleteIntegration(workspaceId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrations'],
        refetchType: 'active'
      });
      queryClient.resetQueries();
    }
  });
}
